import * as moment from "moment";
import Store from "../Store/Store";
import Formatter from "../Localization/Formatter";

class Time {
  private instance: Date;

  public getDatepickerHint(): string {
    const locale = Store.getConfigurator().getState().app.settings.user.locale.iso;
    const formatHint: string = locale === "en-US" ? "MM/DD/YYYY" : "dd-mm-jjjj";

    return ` (${formatHint})`;
  }

  public now(): Time {
    this.instance = new Date();
    return this;
  }

  public get(): Date {
    return this.instance;
  }

  public momentify(date: Date | null = null): any {
    if (date === null) {
      return moment();
    }

    return moment(date).locale(Store.getConfigurator().getState()?.app?.settings?.user?.locale?.iso);
  }

  public fromNow(date: Date | null = null): any {
    if (!date) {
      return "";
    }

    return moment(date).locale(Store.getConfigurator().getState()?.app?.settings?.user?.locale?.iso).fromNow();
  }
  public isBefore(date1: Date, date2: Date): any {
    if (!date1 || !date2) {
      return "";
    }

    return this.momentify(date1).isBefore(date2);
  }

  public useFormat = (date: Date, format: string): any => {
    if (date === null) {
      return null;
    }

    return this.momentify(date).locale(Store.getConfigurator().getState()?.app?.settings?.user?.locale?.iso).format(format);
  }

  public useFormatForUserLanguage = (date: Date, language: string, format: string): any => {
    if (date === null) {
      return null;
    }

    return this.momentify(date).locale(language).format(format);
  }

  public formatOrNull(date): any {
    if (date === null) {
      return null;
    }

    return this.momentify(date).format("YYYY-MM-DD");
  }

  public firstDateNextMonth(): Date {
    return moment().add(1, "months").startOf("month").toDate();
  }
  public firstDateNextMonthfromDate(date: Date): Date {
    return moment(date).add(1, "months").startOf("month").toDate();
  }

  public nextDayFromDate(date: Date): Date {
    return this.momentify(date).add(1, "days").toDate();
  }

  public weekFromDate(date: Date): Date {
    return this.momentify(date).add(7, "days").toDate();
  }

  // Get the last day of next year and next month.
  public nextYearPrevMonthLastDay(date: Date): Date {
    return this.momentify(date).add(1, "years").subtract(1, "day").toDate();
  }

  public firstDateCurrentMonth(): Date {
    return moment().startOf("month").toDate();
  }

  public firstDateSelectedMonth(date: Date): Date {
    return this.momentify(date).startOf("month").toDate();
  }

  public lastDateCurrentMonth(): Date {
    return moment().endOf("month").toDate();
  }

  public lastDateSelectedMonth(date: Date): Date {
    return this.momentify(date).endOf("month").toDate();
  }

  public firstDatePreviousMonth(): Date {
    return moment().subtract(1, "months").startOf("month").toDate();
  }

  public lastDatePreviousMonth(): Date {
    return moment().subtract(1, "months").endOf("month").toDate();
  }

  public firstDateAnyPreviousMonth(count): Date {
    return moment().subtract(count, "months").startOf("month").toDate();
  }

  public firstDateAnyPreviousMonthString(count): string {
    return moment().subtract(count, "months").startOf("month").format("YYYY-MM-DD");
  }

  public lastDateAnyPreviousMonth(count): string {
    return moment().subtract(count, "months").endOf("month").format("YYYY-MM-DD");
  }

  public firstDatePreviousYear(): Date {
    return moment().subtract(1, "years").startOf("year").toDate();
  }

  public currentMonthStartMinusYears(count: number): Date {
    return moment().subtract(count, "years").startOf("month").toDate();
  }

  public currentDatePlusYears(count: number): Date {
    return moment().add(count, "years").toDate();
  }

  public lastTwelveMonths(): any[] {
    const lastTwelveMonthsArray = [];

    for (let i = 1; i < 13; i++) {
      const symbolPosition = this.firstDateAnyPreviousMonthString(i).indexOf("-");
      const monthNumber = this.firstDateAnyPreviousMonthString(i)
        .slice(symbolPosition + 1, symbolPosition + 3);
      const year = this.firstDateAnyPreviousMonthString(i)
        .slice(0, 4);

      lastTwelveMonthsArray.push({
        value: monthNumber + " " + year,
        dates: {
          firstDate: this.firstDateAnyPreviousMonthString(i),
          lastDate : this.lastDateAnyPreviousMonth(i)
        }
      });
    }

    return lastTwelveMonthsArray;
  }
  public twoYearsForReport(): any[] {
    const settings = Store.getConfigurator().getState().app.settings.user;
    const twoYearsForReport = [
      {
        label: this.momentify(this.firstDatePreviousYear()).locale(settings.locale.iso).format("YYYY"),
        value: this.momentify(this.firstDatePreviousYear()).format("YYYY")
      },
      {
        label: this.momentify(this.currentDatePlusYears(0)).locale(settings.locale.iso).format("YYYY"),
        value: this.momentify(this.currentDatePlusYears(0)).format("YYYY")
      },
    ]
    return twoYearsForReport;
  }

  public sixMonthsForReports(): any[] {
    const settings = Store.getConfigurator().getState().app.settings.user;
    const sixMonthsForReports = [
      {
        label: this.momentify(this.firstDateNextMonth()).locale(settings.locale.iso).format("MMMM YYYY"),
        value: this.momentify(this.firstDateNextMonth()).format("YYYY-MM-DD")
      },
      {
        label: this.momentify(this.firstDateCurrentMonth()).locale(settings.locale.iso).format("MMMM YYYY"),
        value: this.momentify(this.firstDateCurrentMonth()).format("YYYY-MM-DD")
      }
    ];

    for (let i = 1; i < 5; i++) {
      sixMonthsForReports.push({
        label: this.momentify(this.firstDateAnyPreviousMonth(i)).locale(settings.locale.iso).format("MMMM YYYY"),
        value: this.momentify(this.firstDateAnyPreviousMonth(i)).format("YYYY-MM-DD")
      })
    }

    return sixMonthsForReports;
  }

  public getMonthDetail(date: string, detail: string): string | number {
    let monthTitle = "";
    let monthIndex = 0;

    this.lastTwelveMonths().forEach((month, index) => {
      if (date === month.dates.firstDate) {
        monthTitle = month.value;
        monthIndex = index;
      }
    });

    if (detail === "title") {
      return monthTitle;
    }
    return monthIndex;
  }

  public addDays(date: Date, days: number): Date {
    return this.momentify(date).add(days, "days").toDate();
  }

  public substractMonth(date: Date, month: number): Date {
    return this.momentify(date).subtract(month, "months").toDate();
  }

  public addMonth(date: Date, month: number): Date {
    return this.momentify(date).add(month, "months").toDate();
  }

  public useSetForExpirationDate(date: Date, month: number): Date {
    return this.momentify(date).subtract(1, "days").add(month, "months").toDate();
  }

  public diffInDays(first: Date | null, second: Date = new Date()): any {
    if (first === null) {
      return 0;
    }

    return Math.round(Math.abs((first.getTime() - second.getTime()) / (24 * 60 * 60 * 1000)));
  }
  public todayOrEarlier(date: Date | null): any {
    if (date === null) {
      return 0;
    }

    return (new Date(date).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000) <= 0;
  }

  public getTwelveMonths(date: Date | null): any[] {
    const settings = Store.getConfigurator().getState().app.settings.user;
    let arr = []

    if (date === null || settings === null) {
      return arr;
    }

    for (let i = 0; i < 12; i++) {
      arr.push(this.momentify(date).add(i, "months").locale(settings.locale.iso).format("MMM"));
    }

    return arr;
  }

  public formatTwoDatesPeriod = (firstDate: Date, lastDate: Date) => {
    const localeIso = Store.getConfigurator().getState().app.settings.user.locale.iso;

    if (!firstDate || !lastDate) {
      return `${Formatter.formatDate(firstDate)} - ${Formatter.formatDate(lastDate)}`;
    }
    if (firstDate.getFullYear() === lastDate.getFullYear()) {
      if (firstDate.getMonth() === lastDate.getMonth()) {
        if (localeIso === "en-US") {
          return `${this.momentify(firstDate).locale(localeIso).format("MMMM")} ${this.momentify(firstDate).format("DD")} - ${this.momentify(lastDate).format("DD")}, ${this.momentify(firstDate).locale(localeIso).format("YYYY")}`;
        }
        return `${this.momentify(firstDate).format("DD")} - ${this.momentify(lastDate).format("DD")} ${this.momentify(firstDate).locale(localeIso).format("MMMM YYYY")}`;
      }
      return `${this.momentify(firstDate).format("DD MMMM")} - ${this.momentify(lastDate).format("DD MMMM")} ${this.momentify(firstDate).locale(localeIso).format("YYYY")}`;
    }

    return `${Formatter.formatDate(firstDate)} - ${Formatter.formatDate(lastDate)}`;
  }

  public formatTwoDatesPeriodForLanguage = (firstDate: Date, lastDate: Date, language: string) => {

    if (!firstDate || !lastDate) {
      return `${Formatter.formatDate(firstDate)} - ${Formatter.formatDate(lastDate)}`;
    }
    if (firstDate.getFullYear() === lastDate.getFullYear()) {
      if (firstDate.getMonth() === lastDate.getMonth()) {
        if (language === "en-US") {
          return `${this.momentify(firstDate).locale(language).format("MMMM")} ${this.momentify(firstDate).format("DD")} - ${this.momentify(lastDate).format("DD")}, ${this.momentify(firstDate).locale(language).format("YYYY")}`;
        }
        return `${this.momentify(firstDate).format("DD")} - ${this.momentify(lastDate).format("DD")} ${this.momentify(firstDate).locale(language).format("MMMM YYYY")}`;
      }
      return `${this.momentify(firstDate).format("DD MMMM")} - ${this.momentify(lastDate).format("DD MMMM")} ${this.momentify(firstDate).locale(language).format("YYYY")}`;
    }

    return `${Formatter.formatDate(firstDate)} - ${Formatter.formatDate(lastDate)}`;
  }
}

export default new Time();
